/**
 * Request-scoped MCP tools for Codable project chat.
 *
 * Live gets the real project surface (workspace, git, preview, versions,
 * parked branches, CI). Ask mode stays read + memory; writes go through
 * patch/commit cards. God mode is an admin-only bonus for chat development,
 * not the path for project work.
 */

export const CODABLE_CHAT_READ_TOOLS = [
  'codable_chat_file_read',
  'codable_files',
  'codable_workspace_search',
  'codable_workspace_status',
  'codable_project_status',
  'codable_history',
  'codable_project_get',
  'codable_project_log',
  'codable_security_results',
  'codable_security_scan',
  'codable_logs',
  'codable_ocr',
  'codable_platform_call',
  'codable_chat_search',
  'codable_plan_history',
  'codable_plan_export_md',
  'codable_library_search',
  'codable_library_read',
  'codable_web_search',
  'codable_web_fetch',
  'codable_template_list',
  'codable_agent_memory_get',
  'codable_gitlab_check',
] as const;

export const CODABLE_CHAT_MEMORY_WRITE_TOOLS = [
  'codable_agent_memory_put',
] as const;

export const CODABLE_CHAT_WRITE_TOOLS = [
  'codable_workspace_replace',
  'codable_workspace_write',
  // Úklid v workspace (nahraný asset, omylem vytvořený soubor). Symetrie k write:
  // co jde vytvořit, musí jít i smazat — Filip 2026-09-06 („ať se to nehromadí").
  'codable_workspace_delete',
  'codable_workspace_heal',
  'codable_preview_rebuild',
  'codable_library_write',
  'codable_library_ingest',
  'codable_plan_import_md',
] as const;

export const CODABLE_CHAT_GIT_TOOLS = [
  'codable_commit',
  // Vrátit se zpátky. Bez toho agent uměl workspace jen rozdělat: nezapsané
  // změny ani lokální commity napřed proti GitLabu neměl čím srovnat a posílal
  // člověka na uzel (Filip 2026-09-13: „nemám nástroj na git reset --hard").
  'codable_workspace_discard',
  // Chráněná hlavní větev není slepá ulička — práce se dá nabídnout ke sloučení
  // místo „pod tvým účtem nelze zapisovat do main".
  'codable_merge_request',
] as const;

export const CODABLE_CHAT_GOD_TOOLS = [
  'codable_god_read',
  'codable_god_write',
  'codable_god_deploy',
  'codable_god_status',
] as const;

/** Project lifecycle that a working agent needs. RBAC still applies at MCP. */
export function projectWorkTools(orchestratorProjectId: string | null): string[] {
  const tools = [
    'apphost.workspace_decide',
    'gitlab.worker_resolve',
    'gitlab.ci_enable',
    'orchestrator.project_operation_get',
    // Co v projektu selhalo: fronta operací (sync/promote/build) i s důvodem.
    // Bez toho agent o selhané publikaci nevěděl, dokud se ho někdo nezeptal
    // (Filip 2026-09-13: „nemělo by to umět načíst i chyby?").
    'orchestrator.project_operation_list',
    // Debug prostředí, pro které píše: odpovídá vůbec ta adresa, a čím?
    'debug.http_probe',
  ];
  if (orchestratorProjectId) {
    const id = orchestratorProjectId;
    tools.push(
      `${id}.orchestrator.sync`,
      `${id}.orchestrator.promote`,
      `${id}.apphost.release_switch`,
      `${id}.apphost.preview_set`,
      `${id}.gitlab.worker_resolve`,
      `${id}.gitlab.ci_enable`,
      // Build commitu jako operace projektu — agent po commitu vidí, jak
      // dopadl, místo aby hádal (viz orchestrator/project-build-watch.ts).
      `${id}.orchestrator.build`,
    );
  }
  return tools;
}

export function buildCodableChatTools(opts: {
  autonomyMode: 'ask' | 'workspace';
  godActive: boolean;
  orchestratorProjectId: string | null;
  vision: boolean;
}): string[] {
  const read = CODABLE_CHAT_READ_TOOLS.filter((tool) =>
    opts.vision ? tool !== 'codable_ocr' : true
  );
  const out: string[] = [
    ...read,
    ...(opts.vision ? ['codable_preview_screenshot'] : []),
    ...CODABLE_CHAT_MEMORY_WRITE_TOOLS,
  ];
  if (opts.autonomyMode === 'workspace') {
    out.push(
      ...CODABLE_CHAT_WRITE_TOOLS,
      ...CODABLE_CHAT_GIT_TOOLS,
      ...projectWorkTools(opts.orchestratorProjectId),
    );
  }
  if (opts.godActive) out.push(...CODABLE_CHAT_GOD_TOOLS);
  return out;
}

export function isCodableChatWriteTool(name: string): boolean {
  const n = name.replaceAll('.', '_');
  return (
    n === 'codable_workspace_write' ||
    n === 'codable_workspace_replace' ||
    n === 'codable_workspace_delete' ||
    n === 'codable_workspace_heal' ||
    n === 'codable_library_write' ||
    n === 'codable_library_ingest' ||
    n === 'codable_plan_import_md' ||
    n === 'codable_agent_memory_put' ||
    n === 'codable_god_write' ||
    n === 'apphost_workspace_decide' ||
    name.endsWith('.apphost.preview_set') ||
    name.endsWith('_apphost_preview_set')
  );
}

export function isCodableChatGitTool(name: string): boolean {
  const n = name.replaceAll('.', '_');
  return (
    n === 'codable_commit' ||
    n === 'codable_workspace_discard' ||
    n === 'codable_merge_request' ||
    n === 'gitlab_ci_enable' ||
    name.endsWith('.gitlab.ci_enable') ||
    n.endsWith('_gitlab_ci_enable') ||
    name.endsWith('.orchestrator.promote') ||
    name.endsWith('.orchestrator.sync') ||
    name.endsWith('.apphost.release_switch') ||
    n.endsWith('_orchestrator_promote') ||
    n.endsWith('_orchestrator_sync') ||
    n.endsWith('_apphost_release_switch')
  );
}
